import './Tooltip.css';
import '../style.css';

import React from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AuthorisedRoute, ErrorBoundary, NotFound, useApplicationContext } from '@economic/react-app';
import Header from './Header';
import * as pages from '../pages';
import isWindowUndocked from '../shared/util/utilities/isWindowUndocked';
import Loader from '../shared/components/Loader';
import { Provider as DataProvider } from '../shared/components/DataContext';
import { DialogProvider } from '../shared/components/DialogManagerContext';
import { useEconomic } from './useEconomic';
import TrialExpiredDialog from '../shared/components/modals/TrialExpiredDialog';
import VismaConnectCongratsDialog from '../shared/components/modals/VismaConnectCongratsDialog';
import VismaConnectJoinDialog from '../shared/components/modals/VismaConnectJoinDialog';
import RequestRemoteAccessDialog from '../shared/components/modals/RequestRemoteAccessDialog';
import { useIntroductionTour } from './Header/IntroductionTour/useIntroductionTour';
import GettingStartedDialog from '../forms/GettingStartedSplash/GettingStartedDialog';
import LegacyDialogRoutes from '../shared/LegacyDialogRoutes';
import { useUnless } from './useUnless';

function BaseRoute() {
    const {
        configuration: { routes },
    } = useApplicationContext();
    const baseRoute = routes['/'];

    if (baseRoute && baseRoute[0] === '/') {
        return <Navigate to={baseRoute} />;
    } else if (baseRoute === 'AppStore/developer') {
        // temporary fix
        return <Navigate to="developer" />;
    }

    return <Navigate to="home" />;
}

export default function Economic() {
    const showHeader = isWindowUndocked() === false;
    const loading = useEconomic();
    const { pathname } = useLocation();
    useUnless();

    // Whenever path is changed, by taking away focus for link elements and focussing body we allow taco tables to
    // hijack keyboard navigation if they want to.
    // See https://github.com/e-conomic/gaudi/blob/master/packages/gaudi/src/components/Table/hooks/useTableKeyboardNavigation.ts#L26
    // and https://github.com/ReactTraining/react-router/issues/5210
    React.useEffect(() => {
        document.body.focus();
    }, [pathname]);

    if (loading) {
        return <Loader />;
    }

    return (
        <DialogProvider>
            <DataProvider>
                {showHeader && <Header />}
                <ErrorBoundary>
                    <Routes>
                        <AuthorisedRoute path="/" element={<BaseRoute />} />

                        <AuthorisedRoute path="accounting/*" element={<pages.Accounting />} />
                        <AuthorisedRoute path="admin/*" element={<pages.Admin />} />
                        <AuthorisedRoute path="basic/*" element={<pages.Basic />} />
                        <AuthorisedRoute path="custom/*" element={<pages.Custom />} />
                        <AuthorisedRoute path="developer" element={<pages.Developer />} />
                        <AuthorisedRoute path="e-signature" element={<pages.ESignature />} />
                        <AuthorisedRoute path="home" element={<pages.Home />} />
                        <AuthorisedRoute path="inventory/*" element={<pages.Inventory />} />
                        <AuthorisedRoute path="journal-pro" element={<pages.JournalPro />} />
                        <AuthorisedRoute path="market/*" element={<pages.Market />} />
                        <AuthorisedRoute path="partner-api" element={<pages.PartnerApi />} />
                        <AuthorisedRoute path="projects/*" element={<pages.Projects />} />
                        <AuthorisedRoute path="reports/*" element={<pages.Reports />} />
                        <AuthorisedRoute path="sales/*" element={<pages.Sales />} />
                        <AuthorisedRoute path="settings/*" element={<pages.Settings />} />
                        <AuthorisedRoute path="system/*" element={<pages.System />} />
                        <AuthorisedRoute path="user/*" element={<pages.User />} />

                        {/* modules */}
                        <AuthorisedRoute path="inbox/*" element={<pages.Inbox />} />
                        <AuthorisedRoute path="webshop/*" element={<pages.Webshop />} />
                        <AuthorisedRoute path="webshop-plus" element={<pages.WebshopPlus />} />
                        <AuthorisedRoute path="website" element={<pages.Website />} />

                        <AuthorisedRoute path="logout" element={<pages.Logout />} />

                        <Route path="404" element={<NotFound />} />
                        <Route path="*" element={<NotFound />} />
                    </Routes>

                    <TrialExpiredDialog />
                    <GettingStartedDialogWrapper />
                    <VismaConnectCongratsDialog />
                    <VismaConnectJoinDialog />
                    <RequestRemoteAccessDialog />

                    <LegacyDialogRoutes />
                </ErrorBoundary>
            </DataProvider>
        </DialogProvider>
    );
}

/**
 * Wrap GettingStartedDialog and trigger home tour after it is done.
 * This component must be under EContext context provider.
 */
function GettingStartedDialogWrapper() {
    const [, setShowTour] = useIntroductionTour();

    function gettingStartedFinished() {
        setShowTour(true);
    }

    return <GettingStartedDialog onDone={gettingStartedFinished} />;
}
